import NiceModal, { useModal } from '@ebay/nice-modal-react';

import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import AutoComplete from 'components/field/AutoComplete';
import Button from 'components/field/Button';
import EnumSelect from 'components/field/EnumSelect';
import StringInput from 'components/field/StringInput';
import Form from 'components/form/Form';
import FormItem from 'components/form/FormItem';
import Alert from 'components/global/Alert';
import ConfirmationModal from 'components/global/ConfirmationModal';
import Modal from 'components/global/Modal';
import Typography from 'components/global/Typography';
import { ModalActions, ModalContent, ModalTitle } from 'components/global/styled/Modal.styled';
import { updateDynamicListsInPage } from 'components/list/dynamicListSlice';

import useAccessControl from 'hooks/useAccessControl';
import useAlert from 'hooks/useAlert';
import useAxiosPrivate from 'hooks/useAxiosPrivate';

import { AUTOCOMPLETE, ORDER_ACTIONS } from 'services/consts';

import AppLogger from 'utils/AppLogger';

import { faExchange } from '@fortawesome/pro-solid-svg-icons';

/**
 * @author Burak Ak
 */

const Reallocate = NiceModal.create(({ otoOrderIDs, orderIDs }) => {
	const modal = useModal();
	const axios = useAxiosPrivate();
	const dispatch = useDispatch();
	const [isLoading, setIsLoading] = useState(false);
	const { showError, showSuccess } = useAlert();

	const isAuthorized = useAccessControl();
	const defaultWarehouseId = useSelector((state) => state.user.generalSettings.defaultWarehouse);

	const formMethods = useForm({
		defaultValues: { warehouseID: null, branchID: null }
	});

	const { handleSubmit, setValue, watch } = formMethods;
	const { t } = useTranslation();

	const onSubmit = async (data) => {
		setIsLoading(true);
		try {
			const response = await axios.post(ORDER_ACTIONS.REALLOCATE_ORDER, {
				otoOrderIDs: Object.values(otoOrderIDs),
				...data
			});
			if (response.status === 200) {
				let hasSuccess = false;
				response.data.forEach((item) => {
					if (item?.errorMsg) {
						showError({ message: t(item.errorMsg, { OrderIDS: item.orderIds }) });
					} else if (item.success) {
						hasSuccess = true;
					}
				});
				if (hasSuccess) {
					showSuccess();
					dispatch(updateDynamicListsInPage());
				} else {
					AppLogger.error(response);
				}
			} else {
				AppLogger.error(response);
			}
		} catch (error) {
			AppLogger.error(error);
			showError();
		}
		setIsLoading(false);
		modal.remove();
	};

	useEffect(() => {
		let orderNumbers = '';
		orderIDs.forEach((orderID, index) => {
			orderNumbers += orderID + (index !== orderIDs.length - 1 ? ', ' : '');
		});
		setValue('orderNumbers', orderNumbers);
	}, [orderIDs]);

	const warehouseAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const warehouseAutocompleteLabels = useMemo(() => ['Name'], []);
	const warehouseAutoComplete = watch('warehouseID');

	const branchAutocompleteColumns = [
		{ accessor: 'Name', width: 200 },
		{ accessor: 'Code', width: 150 }
	];
	const branchAutocompleteLabels = useMemo(() => ['Name'], []);
	const branchAutoComplete = watch('branchID');

	const defaultColSpan = {
		md: 6
	};

	useEffect(() => {
		if (warehouseAutoComplete === null && branchAutoComplete === null) {
			setValue('orderStatus', null);
		}
	}, [warehouseAutoComplete, branchAutoComplete]);

	useEffect(() => {
		if (!isAuthorized('MultiWarehouseModule') && !isAuthorized('BranchModule')) {
			setValue('orderStatus', 'assignedToWarehouse');
			setValue('warehouseID', defaultWarehouseId);
		}
	}, [isAuthorized]);

	const orderStatusTypes = useMemo(() => {
		const types = [];

		if (warehouseAutoComplete !== null) {
			types.push('assignedToWarehouse');
		}

		if (branchAutoComplete !== null) {
			types.push('branchAssigned');
		}

		if (isAuthorized('ClickAndCollectModule')) {
			types.push('pickupFromStore');
		}

		return types;
	}, [warehouseAutoComplete, branchAutoComplete]);

	return (
		<Modal maxWidth="medium" fullwidth className="modal-reallocate">
			<ModalTitle>
				<Typography variant="p1">reallocate</Typography>
			</ModalTitle>
			<ModalContent>
				<Form formMethods={formMethods}>
					<Alert content="reallocateInfoText" showIcon />

					<FormItem label="orderNumbers" colSpan={12}>
						<StringInput name="orderNumbers" disabled={true} />
					</FormItem>

					<FormItem colSpan={defaultColSpan} label="warehouse" roleName="MultiWarehouseModule">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.WAREHOUSES}
							columns={warehouseAutocompleteColumns}
							selectionLabelAccessor={warehouseAutocompleteLabels}
							value={warehouseAutoComplete}
							setValue={(value) => {
								const warehouseValue = value?.ID ?? null;
								setValue('warehouseID', warehouseValue, { shouldValidate: true, shouldDirty: true });
								if (warehouseValue !== null) {
									setValue('branchID', null);
									setValue('orderStatus', 'assignedToWarehouse');
								}
							}}
						/>
					</FormItem>
					<FormItem colSpan={defaultColSpan} label="branches" roleName="BranchModule">
						<AutoComplete
							autoCompleteName={AUTOCOMPLETE.BRANCHES}
							columns={branchAutocompleteColumns}
							selectionLabelAccessor={branchAutocompleteLabels}
							value={branchAutoComplete}
							setValue={(value) => {
								const branchValue = value?.ID ?? null;
								setValue('branchID', branchValue, { shouldValidate: true, shouldDirty: true });
								if (branchValue !== null) {
									setValue('warehouseID', null);
									setValue('orderStatus', 'branchAssigned');
								}
							}}
						/>
					</FormItem>

					<FormItem label="orderStatus" colSpan={defaultColSpan}>
						<EnumSelect
							searchable={false}
							data={orderStatusTypes}
							disabled={
								(!isAuthorized('MultiWarehouseModule') && !isAuthorized('BranchModule')) ||
								(warehouseAutoComplete === null && branchAutoComplete === null)
							}
							value={watch('orderStatus')}
							name={`orderStatus`}
							setValue={(value) => {
								setValue(`orderStatus`, value, { shouldValidate: true, shouldDirty: true });
							}}
						/>
					</FormItem>
				</Form>
			</ModalContent>
			<ModalActions>
				<Button
					label="cancel"
					onClick={() => {
						NiceModal.show(ConfirmationModal, {
							yesAction: () => {
								modal.remove();
							}
						});
					}}
					variant="outline"
				/>

				<Button
					onClick={handleSubmit(onSubmit, (errors) => {
						AppLogger.log(errors);
					})}
					icon={faExchange}
					loading={isLoading}
					label="reallocate"
					variant="blue"
				/>
			</ModalActions>
		</Modal>
	);
});

export default Reallocate;
